import { useCallback, useEffect, useState } from 'react';
import Breadcrumbs from '../Components/Breadcrumbs';
import { ConfirmationProvider } from '../Components/ConfirmationContext';
import Table from '../Components/Tables';
import { formatDateUTC } from '../utils/formatDate';

import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Button from '../Components/Buttons';
import DateRangeSelector, { getDateRangeByPeriod } from '../Components/DateRangeSelector';
import { Tab, Tabs } from '../Components/Tabs';
import { CustomSelect } from '../Components/CustomFormElements';
import {
  AuditOperationsApiProvider,
  useAuditOperationsApi,
} from '../Services/AuditOperationsApiProvider';
import { LlmLogApiProvider, useLlmLogApi } from '../Services/LlmLogApiProvider';
import {
  ConnectionLogApiProvider,
  useConnectionLogApi,
} from '../Services/ConnectionLogApiProvider';
import { useAuth } from '../Contexts/AuthContext';
import { getTablePaginationFromStorage } from '../utils/tablePaginationStorage';
import { formatTableTitle } from '../utils/formatTitle';

const resolveRange = (start, end) =>
  start ? { start_date: start, end_date: end } : getDateRangeByPeriod('today');

const TABS = {
  OPERATIONS: 'operations',
  LLM: 'llm',
  CONNECTIONS: 'connections',
};

const AUDIT_COLUMNS = [
  { name: 'ID', selector: (row) => row.key, sortable: true },
  { name: 'User Key', selector: (row) => row.user_key, sortable: true },
  { name: 'Operation Type', selector: (row) => row.operation_type, sortable: true },
  { name: 'Created', selector: (row) => formatDateUTC(row.created_at), sortable: true },
  { name: 'Updated', selector: (row) => formatDateUTC(row.updated_at), sortable: true },
];

const LLM_COLUMNS = [
  { name: 'Model', selector: (row) => row.model_name, sortable: true },
  { name: 'Total Tokens', selector: (row) => row.total_tokens, sortable: true },
  { name: 'Input Tokens', selector: (row) => row.input_tokens, sortable: true },
  { name: 'Output Tokens', selector: (row) => row.output_tokens, sortable: true },
  {
    name: 'Cost',
    selector: (row) => row.cost,
    sortable: true,
    format: (row) => {
      if (row.cost === null || row.cost === undefined) {
        return 'Not calculated';
      }

      return `${Number(row.cost).toFixed(2)} $`;
    },
  },
];

const LLM_REQUEST_COLUMNS = [
  { name: 'ID', selector: (row) => row.id, sortable: true },
  { name: 'Model', selector: (row) => row.model_name, sortable: true },
  { name: 'Usage Type', selector: (row) => row.usage_type, sortable: true },
  {
    name: 'Duration, s',
    selector: (row) => (row.duration != null ? Number(row.duration).toFixed(2) : '-'),
    sortable: true,
  },
  { name: 'Total Tokens', selector: (row) => row.total_tokens, sortable: true },
  { name: 'Input Tokens', selector: (row) => row.input_tokens, sortable: true },
  { name: 'Output Tokens', selector: (row) => row.output_tokens, sortable: true },
  { name: 'Created', selector: (row) => formatDateUTC(row.created_at), sortable: true },
];

const CONNECTION_REQUEST_COLUMNS = [
  { name: 'ID', selector: (row) => row.id, sortable: true },
  { name: 'Connection', selector: (row) => row.connection_name, sortable: true },
  { name: 'Type', selector: (row) => row.connection_type, sortable: true },
  { name: 'Operation', selector: (row) => row.operation, sortable: true },
  { name: 'Status', selector: (row) => row.status, sortable: true },
  {
    name: 'Duration, s',
    selector: (row) => (row.duration != null ? Number(row.duration).toFixed(2) : '-'),
    sortable: true,
  },
  { name: 'Created', selector: (row) => formatDateUTC(row.created_at), sortable: true },
];

function AuditOperationsHub({ canEdit }) {
  const { getAuditOperations, getAuditOperationTypes, exportAuditOperations } =
    useAuditOperationsApi();
  const { organizationId } = useParams();
  const {
    getLlmLogs,
    getLlmLogModels,
    getLlmLogUsageTypes,
    getTokenAnalyticsByLLM,
    getTokenAnalytics,
  } = useLlmLogApi();
  const { getConnectionLogs } = useConnectionLogApi();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [auditOperations, setAuditOperations] = useState([]);
  const [totalAuditOperations, setTotalAuditOperations] = useState(0);
  const navigate = useNavigate();
  const { status: authStatus } = useAuth();

  const hasLlmLogFlag = authStatus?.feature_flags?.LLM_LOG === true;

  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const activeTab =
    requestedTab === TABS.CONNECTIONS
      ? TABS.CONNECTIONS
      : hasLlmLogFlag && requestedTab === TABS.LLM
        ? TABS.LLM
        : TABS.OPERATIONS;

  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();

  const [searchOptions, setSearchOptions] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);

  const hasOrganizationsPermission = authStatus?.permissions?.includes('all:organizations');

  const [tokenAnalytics, setTokenAnalytics] = useState(null);
  const [tokenAnalyticsByLLM, setTokenAnalyticsByLLM] = useState([]);
  const [totalTokenCount, setTotalTokenCount] = useState(0);

  const [tokenStartDate, setTokenStartDate] = useState();
  const [tokenEndDate, setTokenEndDate] = useState();
  const [llmTokenStartDate, setLlmTokenStartDate] = useState();
  const [llmTokenEndDate, setLlmTokenEndDate] = useState();

  const [llmLogs, setLlmLogs] = useState([]);
  const [totalLlmLogs, setTotalLlmLogs] = useState(0);
  const [llmLogsLoading, setLlmLogsLoading] = useState(false);
  const [llmLogsError, setLlmLogsError] = useState(null);
  const [llmLogStartDate, setLlmLogStartDate] = useState();
  const [llmLogEndDate, setLlmLogEndDate] = useState();
  const [modelOptions, setModelOptions] = useState([]);
  const [usageTypeOptions, setUsageTypeOptions] = useState([]);
  const [llmFilterReset, setLlmFilterReset] = useState(false);

  const [connectionLogs, setConnectionLogs] = useState([]);
  const [totalConnectionLogs, setTotalConnectionLogs] = useState(0);
  const [connectionLogsLoading, setConnectionLogsLoading] = useState(false);
  const [connectionLogsError, setConnectionLogsError] = useState(null);
  const [connectionLogStartDate, setConnectionLogStartDate] = useState();
  const [connectionLogEndDate, setConnectionLogEndDate] = useState();

  const tableTitle = 'Audit Operations';
  const { persistedPageSize, persistedSkip } = getTablePaginationFromStorage(
    formatTableTitle(tableTitle),
  );

  const tableTokensTitle = 'Models';
  const { persistedPageSize: persistedTokensPageSize, persistedSkip: persistedTokensSkip } =
    getTablePaginationFromStorage(formatTableTitle(tableTokensTitle));

  const tableRequestsTitle = 'Requests';
  const { persistedPageSize: persistedRequestsPageSize, persistedSkip: persistedRequestsSkip } =
    getTablePaginationFromStorage(formatTableTitle(tableRequestsTitle));

  const tableConnectionRequestsTitle = 'Connection Requests';
  const {
    persistedPageSize: persistedConnectionRequestsPageSize,
    persistedSkip: persistedConnectionRequestsSkip,
  } = getTablePaginationFromStorage(formatTableTitle(tableConnectionRequestsTitle));

  const searchFilterName = `${formatTableTitle(tableRequestsTitle)}_search_filter`;
  const usageFilterName = `${formatTableTitle(tableRequestsTitle)}_usage_filter`;

  const getStoredFilter = (key) => {
    try {
      return JSON.parse(localStorage.getItem(key) ?? 'null') ?? undefined;
    } catch {
      return undefined;
    }
  };

  const [selectedModels, setSelectedModels] = useState(() => getStoredFilter(searchFilterName));
  const [selectedUsageTypes, setSelectedUsageTypes] = useState(() =>
    getStoredFilter(usageFilterName),
  );

  const handleTabChange = useCallback(
    (value) => {
      const nextParams = new URLSearchParams(searchParams);
      if (value === TABS.OPERATIONS) nextParams.delete('tab');
      else nextParams.set('tab', value);
      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const handlePeriodChange = useCallback((period) => {
    setStartDate(period?.start_date);
    setEndDate(period?.end_date);
  }, []);

  const fetchAuditOperationTypes = async () => {
    try {
      return await getAuditOperationTypes();
    } catch (error) {
      console.error('Error fetching audit operation types:', error);
    }
  };

  const fetchAuditOperations = async (skip, limit, type = selectedTypes) => {
    try {
      setLoading(true);
      const { start_date, end_date } = resolveRange(startDate, endDate);
      const response = await getAuditOperations(skip, limit, start_date, end_date, type);
      setAuditOperations(response.items);
      setTotalAuditOperations(response.totalCount);
    } catch (error) {
      console.error('Error fetching audit operations:', error);
      setError('Failed to load audit operations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAuditOperations(persistedSkip, persistedPageSize);
  }, [startDate, endDate]);

  useEffect(() => {
    const loadTypes = async () => {
      const types = await fetchAuditOperationTypes();
      if (!types) return;
      const sortedTypes = [...types].sort((a, b) => a.localeCompare(b));
      const options = sortedTypes.map((type) => ({ label: type, value: type }));
      setSearchOptions(options);
    };
    loadTypes();
  }, []);

  const handleRowClicked = (row) => {
    navigate(`/organizations/${organizationId}/audit-operations/${row.key}`);
  };

  const handleTypeChange = async (skip, limit, type) => {
    const nextTypes = Array.isArray(type) ? type : type ? [type] : [];

    setSelectedTypes(nextTypes);
    await fetchAuditOperations(skip, limit, nextTypes);
  };

  const handleExport = async () => {
    try {
      const { start_date, end_date } = resolveRange(startDate, endDate);
      // якщо використовуєш мультиселект типів — передай сюди масив або null
      await exportAuditOperations(start_date, end_date, selectedTypes ?? null);
    } catch (error) {
      console.error('Error exporting audit operations:', error);
    }
  };

  const fetchTokenAnalyticsByLLM = async (skip, limit) => {
    const { start_date, end_date } = resolveRange(llmTokenStartDate, llmTokenEndDate);

    try {
      const response = await getTokenAnalyticsByLLM(start_date, end_date, skip, limit);
      setTokenAnalyticsByLLM(response.items || []);
      setTotalTokenCount(response.totalCount || 0);
    } catch (error) {
      console.error('Error fetching token analytics:', error);
    }
  };

  useEffect(() => {
    if (hasLlmLogFlag && hasOrganizationsPermission) {
      fetchTokenAnalyticsByLLM(persistedTokensSkip, persistedTokensPageSize);
    }
  }, [
    llmTokenStartDate,
    llmTokenEndDate,
    getTokenAnalyticsByLLM,
    authStatus?.permissions,
    hasLlmLogFlag,
  ]);

  const fetchTokenAnalytics = async () => {
    const { start_date, end_date } = resolveRange(tokenStartDate, tokenEndDate);
    try {
      const analyticsData = await getTokenAnalytics(start_date, end_date);
      setTokenAnalytics(analyticsData);
    } catch (error) {
      console.error('Error fetching total analytics:', error);
    }
  };

  useEffect(() => {
    if (!hasLlmLogFlag) return;
    fetchTokenAnalytics();
  }, [tokenStartDate, tokenEndDate, getTokenAnalytics, hasLlmLogFlag]);

  const handleLlmTokenPeriodChange = useCallback((period) => {
    setLlmTokenStartDate(period?.start_date);
    setLlmTokenEndDate(period?.end_date);
  }, []);

  const handleTokenPeriodChange = useCallback((period) => {
    setTokenStartDate(period?.start_date);
    setTokenEndDate(period?.end_date);
  }, []);

  const handleLlmLogPeriodChange = useCallback((period) => {
    setLlmLogStartDate(period?.start_date);
    setLlmLogEndDate(period?.end_date);
  }, []);

  const fetchLlmLogs = async (
    skip,
    limit,
    model = selectedModels,
    usageType = selectedUsageTypes,
  ) => {
    try {
      setLlmLogsLoading(true);
      const { start_date, end_date } = resolveRange(llmLogStartDate, llmLogEndDate);
      const response = await getLlmLogs(skip, limit, start_date, end_date, model, usageType);
      setLlmLogs(response.items || []);
      setTotalLlmLogs(response.totalCount || 0);
      setLlmLogsError(null);
    } catch (error) {
      console.error('Error fetching llm logs:', error);
      setLlmLogsError('Failed to load LLM requests');
    } finally {
      setLlmLogsLoading(false);
    }
  };

  useEffect(() => {
    console.log(llmLogStartDate, llmLogEndDate);

    if (!hasLlmLogFlag || llmLogStartDate === undefined || llmLogEndDate === undefined) {
      return;
    }
    fetchLlmLogs(persistedRequestsSkip, persistedRequestsPageSize);
  }, [llmLogStartDate, llmLogEndDate, hasLlmLogFlag]);

  useEffect(() => {
    if (!hasLlmLogFlag) return;
    const loadModels = async () => {
      try {
        const { start_date, end_date } = resolveRange(llmLogStartDate, llmLogEndDate);
        const models = await getLlmLogModels(start_date, end_date);
        setModelOptions((models || []).map((model) => ({ label: model, value: model })));
      } catch (error) {
        console.error('Error fetching llm models:', error);
      }
    };
    loadModels();
  }, [llmLogStartDate, llmLogEndDate, getLlmLogModels, hasLlmLogFlag]);

  useEffect(() => {
    if (!hasLlmLogFlag) return;
    const loadUsageTypes = async () => {
      try {
        const { start_date, end_date } = resolveRange(llmLogStartDate, llmLogEndDate);
        const usageTypes = await getLlmLogUsageTypes(start_date, end_date);
        setUsageTypeOptions(
          (usageTypes || []).map((usageType) => ({ label: usageType, value: usageType })),
        );
      } catch (error) {
        console.error('Error fetching llm usage types:', error);
      }
    };
    loadUsageTypes();
  }, [llmLogStartDate, llmLogEndDate, getLlmLogUsageTypes, hasLlmLogFlag]);

  const handleModelChange = async (skip, limit, model) => {
    const nextModels = Array.isArray(model) ? model : model ? [model] : [];
    const safeModels = modelOptions
      .filter((option) => nextModels.includes(option.value))
      .map((option) => option.value);

    localStorage.setItem(searchFilterName, JSON.stringify(safeModels));
    setSelectedModels(safeModels);
    await fetchLlmLogs(0, limit, safeModels, selectedUsageTypes);
  };

  const handleUsageTypeChange = async (usageType) => {
    const nextUsageTypes = Array.isArray(usageType) ? usageType : usageType ? [usageType] : [];
    const safeUsageTypes = usageTypeOptions
      .filter((option) => nextUsageTypes.includes(option.value))
      .map((option) => option.value);

    localStorage.setItem(usageFilterName, JSON.stringify(safeUsageTypes));
    setSelectedUsageTypes(safeUsageTypes);
    setLlmFilterReset((currentValue) => !currentValue);
    await fetchLlmLogs(0, persistedRequestsPageSize, selectedModels, safeUsageTypes);
  };

  const llmRequestUrl = (row) =>
    `/organizations/${organizationId}/audit-operations/llm-requests/${row.id}`;

  const handleLlmLogRowClicked = (row) => {
    navigate(llmRequestUrl(row));
  };

  const handleConnectionLogPeriodChange = useCallback((period) => {
    setConnectionLogStartDate(period?.start_date);
    setConnectionLogEndDate(period?.end_date);
  }, []);

  const fetchConnectionLogs = async (skip, limit) => {
    try {
      setConnectionLogsLoading(true);
      const { start_date, end_date } = resolveRange(connectionLogStartDate, connectionLogEndDate);
      const response = await getConnectionLogs(skip, limit, start_date, end_date);
      setConnectionLogs(response.items || []);
      setTotalConnectionLogs(response.totalCount || 0);
      setConnectionLogsError(null);
    } catch (error) {
      console.error('Error fetching connection logs:', error);
      setConnectionLogsError('Failed to load connection requests');
    } finally {
      setConnectionLogsLoading(false);
    }
  };

  useEffect(() => {
    fetchConnectionLogs(persistedConnectionRequestsSkip, persistedConnectionRequestsPageSize);
  }, [connectionLogStartDate, connectionLogEndDate]);

  const connectionRequestUrl = (row) =>
    `/organizations/${organizationId}/audit-operations/connection-requests/${row.id}`;

  const handleConnectionLogRowClicked = (row) => {
    navigate(connectionRequestUrl(row));
  };

  return (
    <div className="container">
      <Breadcrumbs />
      <Tabs activeTab={activeTab} onTabChange={handleTabChange}>
        <Tab label="Operations" value={TABS.OPERATIONS}>
          <div className="row d-flex gap">
            <div className="radius-yes p-2 bg-yes col-12">
              {error ? (
                <div>Error: {error}</div>
              ) : (
                <Table
                  title={tableTitle}
                  columns={AUDIT_COLUMNS}
                  data={auditOperations}
                  selectableRowsHighlight
                  progressPending={loading}
                  paginationPerPage={persistedPageSize}
                  onRowClicked={handleRowClicked}
                  paginationTotalRows={totalAuditOperations}
                  sortIcon={<i className="fa-solid fa-chevron-down"></i>}
                  onChangePage={fetchAuditOperations}
                  getRowUrl={(row) =>
                    `/organizations/${organizationId}/audit-operations/${row.key}`
                  }
                  onChangeFilter={handleTypeChange}
                  typeSearch="multiselect"
                  searchOptions={searchOptions}
                />
              )}
              <div className="d-flex">
                <DateRangeSelector storageKey="audit-operations" onChange={handlePeriodChange} />
                <div style={{ marginLeft: 'auto' }}>
                  <Button variant="primary" size="medium" onClick={handleExport}>
                    Export JSON
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Tab>

        {hasLlmLogFlag && (
          <Tab label="LLM" value={TABS.LLM}>
            <div className="row d-flex gap">
              {hasOrganizationsPermission && tokenAnalytics?.total_tokens != null && (
                <div className="radius-yes p-2 bg-yes col-12">
                  <h2 style={{ fontWeight: 'normal' }}>Total Analytics</h2>
                  <div className="analytics-card" style={{ maxWidth: '500px' }}>
                    <div className="analytics-header">
                      <h3>Total Tokens</h3>
                    </div>
                    <div className="analytics-value">
                      {tokenAnalytics.total_tokens.toLocaleString() || 0}
                    </div>
                    <div className="analytics-details">
                      <div className="detail-item">
                        <span className="label">Input tokens</span>
                        <span className="value">
                          {tokenAnalytics.input_tokens?.toLocaleString() || 0}
                        </span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Output tokens</span>
                        <span className="value">
                          {tokenAnalytics.output_tokens?.toLocaleString() || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div style={{ marginTop: '6vh' }}>
                    <DateRangeSelector
                      storageKey="audit-total"
                      onChange={handleTokenPeriodChange}
                    />
                  </div>
                </div>
              )}

              {hasOrganizationsPermission && (
                <div className="radius-yes p-2 bg-yes col-12">
                  {!tokenAnalyticsByLLM.length ? (
                    <p>No LLM analytics data available.</p>
                  ) : (
                    <Table
                      title={tableTokensTitle}
                      key={`${llmTokenStartDate}-${llmTokenEndDate}`}
                      columns={LLM_COLUMNS}
                      data={tokenAnalyticsByLLM}
                      selectableRowsHighlight
                      paginationPerPage={persistedTokensPageSize}
                      paginationTotalRows={totalTokenCount}
                      sortIcon={<i className="fa-solid fa-chevron-down" />}
                      onChangePage={fetchTokenAnalyticsByLLM}
                    />
                  )}
                  <DateRangeSelector storageKey="audit-llm" onChange={handleLlmTokenPeriodChange} />
                </div>
              )}

              <div className="radius-yes p-2 bg-yes col-12">
                {llmLogsError ? (
                  <div>Error: {llmLogsError}</div>
                ) : (
                  <Table
                    title={tableRequestsTitle}
                    columns={LLM_REQUEST_COLUMNS}
                    data={llmLogs}
                    selectableRowsHighlight
                    progressPending={llmLogsLoading}
                    paginationPerPage={persistedRequestsPageSize}
                    paginationTotalRows={totalLlmLogs}
                    resetPaginationToggle={llmFilterReset}
                    sortIcon={<i className="fa-solid fa-chevron-down" />}
                    onRowClicked={handleLlmLogRowClicked}
                    onChangePage={fetchLlmLogs}
                    getRowUrl={llmRequestUrl}
                    onChangeFilter={handleModelChange}
                    typeSearch="multiselect"
                    searchOptions={modelOptions}
                    defaultValue={selectedModels}
                    headerActions={
                      usageTypeOptions.length ? (
                        <div
                          className="input-block d-flex flex-row justify-between"
                          style={{ flex: '0 0 auto', width: 320 }}
                        >
                          <CustomSelect
                            id="llm-usage-type-filter"
                            label="Usage Type"
                            options={usageTypeOptions}
                            value={selectedUsageTypes}
                            multiSelect={true}
                            onChange={handleUsageTypeChange}
                            placeholder="Select usage types"
                          />
                        </div>
                      ) : null
                    }
                  />
                )}
                <DateRangeSelector
                  storageKey="audit-llm-requests"
                  onChange={handleLlmLogPeriodChange}
                />
              </div>
            </div>
          </Tab>
        )}

        <Tab label="Connections" value={TABS.CONNECTIONS}>
          <div className="row d-flex gap">
            <div className="radius-yes p-2 bg-yes col-12">
              {connectionLogsError ? (
                <div>Error: {connectionLogsError}</div>
              ) : (
                <Table
                  title={tableConnectionRequestsTitle}
                  columns={CONNECTION_REQUEST_COLUMNS}
                  data={connectionLogs}
                  selectableRowsHighlight
                  progressPending={connectionLogsLoading}
                  paginationPerPage={persistedConnectionRequestsPageSize}
                  paginationTotalRows={totalConnectionLogs}
                  sortIcon={<i className="fa-solid fa-chevron-down" />}
                  onRowClicked={handleConnectionLogRowClicked}
                  onChangePage={fetchConnectionLogs}
                  getRowUrl={connectionRequestUrl}
                />
              )}
              <DateRangeSelector
                storageKey="audit-connection-requests"
                onChange={handleConnectionLogPeriodChange}
              />
            </div>
          </div>
        </Tab>
      </Tabs>
    </div>
  );
}

function AuditOperationsWrapper({ canEdit }) {
  return (
    <ConfirmationProvider>
      <AuditOperationsApiProvider>
        <LlmLogApiProvider>
          <ConnectionLogApiProvider>
            <AuditOperationsHub canEdit={canEdit} />
          </ConnectionLogApiProvider>
        </LlmLogApiProvider>
      </AuditOperationsApiProvider>
    </ConfirmationProvider>
  );
}

export default AuditOperationsWrapper;
